import { useMemo } from 'react'

interface Props {
  values: number[]
  width?: number
  height?: number
  className?: string
  /** Stroke + fill color via currentColor — pass `text-primary` etc. on parent. */
  ariaLabel?: string
}

/**
 * Hand-rolled sparkline. Chose this over a chart lib because the Stats page
 * only has 1–2 mini-charts; no need to drag in 30+ KB of recharts.
 *
 * The path is a smooth cubic-bezier curve through `values.length` points.
 * Includes a faint area fill below the line for visual weight.
 */
export function Sparkline({ values, width = 200, height = 48, className, ariaLabel }: Props) {
  const { linePath, areaPath, lastX, lastY, max, min } = useMemo(() => {
    if (values.length === 0) {
      return { linePath: '', areaPath: '', lastX: 0, lastY: 0, max: 0, min: 0 }
    }
    const max = Math.max(...values, 1)
    const min = Math.min(...values, 0)
    const range = max - min || 1
    const stepX = values.length > 1 ? width / (values.length - 1) : 0
    const points = values.map((v, i) => ({
      x: i * stepX,
      y: height - ((v - min) / range) * (height - 4) - 2,
    }))
    // Smooth-ish line via Catmull-Rom-to-Bezier with t=0.5 tension.
    const line = points
      .map((p, i, arr) => {
        if (i === 0) return `M${p.x},${p.y}`
        const prev = arr[i - 1]!
        const cx = (prev.x + p.x) / 2
        return `C${cx},${prev.y} ${cx},${p.y} ${p.x},${p.y}`
      })
      .join(' ')
    const area = `${line} L${points.at(-1)!.x},${height} L0,${height} Z`
    return {
      linePath: line,
      areaPath: area,
      lastX: points.at(-1)!.x,
      lastY: points.at(-1)!.y,
      max, min,
    }
  }, [values, width, height])

  return (
    <svg
      width={width} height={height} viewBox={`0 0 ${width} ${height}`}
      className={className}
      aria-label={ariaLabel}
      role="img"
    >
      <defs>
        <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.18"/>
          <stop offset="100%" stopColor="currentColor" stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#spark-fill)"/>
      <path d={linePath} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      {values.length > 0 && (
        <circle cx={lastX} cy={lastY} r="2.25" fill="currentColor"/>
      )}
      <title>{`max ${max} · min ${min}`}</title>
    </svg>
  )
}
